import type {
    SketchParametricEquation,
    SketchParametricResidualEvaluation,
    SketchParametricResidualUnit,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import type {SketchParametricParameterVector} from './SketchParametricParameterVector';
import {SKETCH_PARAMETRIC_TOLERANCE} from './SketchParametricToleranceConfig';

/**
 * 草图参数方程统一残差评价器。
 *
 * 职责：
 * 1. 在唯一入口校验方程声明的残差行数、量纲数量和有限数值；
 * 2. 为 LM 成本按草图特征长度归一长度残差，角度与无量纲残差保持自然尺度；
 * 3. 以各量纲独立容差计算满足比，避免用“米/弧度混合最大值”判断收敛；
 * 4. 为候选冗余判定复用同一量纲事实源。
 */
export class SketchParametricResidualEvaluator {
    /** 方程不可变依赖声明缓存；WeakMap 避免运行时方程释放后被评价器持有。 */
    private readonly declaredParameterIdsByEquation = new WeakMap<
        SketchParametricEquation,
        ReadonlySet<string>
    >();

    /**
     * 评价完整方程组。
     *
     * @param parameters 参数向量。
     * @param equations 方程列表。
     * @param characteristicLength 本次求解冻结的草图特征长度；省略时从当前参数计算。
     * @returns 原始残差、优化残差、满足比和分量诊断。
     */
    public evaluate(
        parameters: SketchParametricParameterVector,
        equations: readonly SketchParametricEquation[],
        characteristicLength = parameters.calculateCharacteristicLength(),
    ): SketchParametricResidualEvaluation {
        this.assertCharacteristicLength(characteristicLength);

        const rawResiduals: number[] = [];
        const optimizationResiduals: number[] = [];
        const satisfactionRatios: number[] = [];
        const maxRawResidualByUnit = {
            length: 0,
            angle: 0,
            direction: 0,
            dimensionless: 0,
        };
        let maxSatisfactionRatio = 0;

        equations.forEach((equation) => {
            /**
             * parameterIds 是残差与 Jacobian 共同依赖的完整契约。直接在真实读取边界
             * 校验可覆盖解析、数值以及条件分支公式，同时避免在求解热路径创建 Set。
             */
            const read = (parameterId: string): number => {
                this.assertDeclaredParameterDependency(equation, parameterId);

                return parameters.read(parameterId);
            };
            const rows = this.evaluateEquationRows(equation, read);

            rows.forEach((rawResidual, rowIndex) => {
                const unit = equation.residualUnits[rowIndex]!;
                const absoluteRawResidual = Math.abs(rawResidual);
                const satisfactionTolerance = SKETCH_PARAMETRIC_TOLERANCE.residualByUnit[unit];
                const satisfactionRatio = absoluteRawResidual / satisfactionTolerance;

                rawResiduals.push(rawResidual);
                optimizationResiduals.push(
                    this.normalizeForOptimization(rawResidual, unit, characteristicLength),
                );
                satisfactionRatios.push(satisfactionRatio);
                maxRawResidualByUnit[unit] = Math.max(maxRawResidualByUnit[unit], absoluteRawResidual);
                maxSatisfactionRatio = Math.max(maxSatisfactionRatio, satisfactionRatio);
            });
        });

        return {
            rawResiduals,
            optimizationResiduals,
            satisfactionRatios,
            maxSatisfactionRatio,
            maxRawResidualByUnit,
        };
    }

    /**
     * 计算单个候选方程相对冗余容差的最大满足比。
     *
     * @param equation 候选方程。
     * @param read 参数读取函数。
     * @returns 最大冗余容差比；小于等于 1 表示候选与当前解一致。
     */
    public calculateMaxRedundancyRatio(
        equation: SketchParametricEquation,
        read: (parameterId: string) => number,
    ): number {
        const rows = this.evaluateEquationRows(equation, (parameterId) => {
            this.assertDeclaredParameterDependency(equation, parameterId);

            return read(parameterId);
        });

        return rows.reduce((maximum, residual, rowIndex) => {
            const unit = equation.residualUnits[rowIndex]!;
            const tolerance = SKETCH_PARAMETRIC_TOLERANCE.redundancyByUnit[unit];

            return Math.max(maximum, Math.abs(residual) / tolerance);
        }, 0);
    }

    /**
     * 把一个原始物理残差或其导数转换为优化尺度。
     *
     * @param value 原始残差或原始残差对参数的导数。
     * @param unit 残差行量纲。
     * @param characteristicLength 本轮冻结的草图特征长度。
     * @returns 优化尺度下的数值。
     */
    public normalizeForOptimization(
        value: number,
        unit: SketchParametricResidualUnit,
        characteristicLength: number,
    ): number {
        this.assertCharacteristicLength(characteristicLength);

        return value / (unit === 'length' ? characteristicLength : 1);
    }

    /**
     * 评价并校验一条方程的原始残差行。
     *
     * @param equation 方程。
     * @param read 参数读取函数。
     * @returns 原始残差行。
     * @throws 当行数、量纲数量或数值无效时抛出可定位错误。
     */
    private evaluateEquationRows(
        equation: SketchParametricEquation,
        read: (parameterId: string) => number,
    ): readonly number[] {
        const rows = equation.evaluate(read);

        if (rows.length !== equation.residualCount) {
            throw new Error(
                `方程 ${equation.equationId} 残差行数不一致：`
                + `声明 ${equation.residualCount}，实际 ${rows.length}`,
            );
        }
        if (equation.residualUnits.length !== equation.residualCount) {
            throw new Error(
                `方程 ${equation.equationId} 残差量纲数量不一致：`
                + `声明 ${equation.residualCount}，实际 ${equation.residualUnits.length}`,
            );
        }

        rows.forEach((residual, rowIndex) => {
            if (!Number.isFinite(residual)) {
                throw new Error(
                    `方程 ${equation.equationId} 第 ${rowIndex + 1} 行残差不是有限数值：`
                    + `${String(residual)}`,
                );
            }
        });

        return rows;
    }

    /**
     * 校验公式真实读取的参数已经出现在依赖声明中。
     *
     * @param equation 当前评价方程。
     * @param parameterId 公式请求读取的参数 ID。
     * @throws 依赖声明不完整时抛出包含方程和参数 ID 的错误。
     */
    private assertDeclaredParameterDependency(
        equation: SketchParametricEquation,
        parameterId: string,
    ): void {
        let declaredParameterIds = this.declaredParameterIdsByEquation.get(equation);

        if (!declaredParameterIds) {
            declaredParameterIds = new Set(equation.parameterIds);
            this.declaredParameterIdsByEquation.set(equation, declaredParameterIds);
        }

        if (!declaredParameterIds.has(parameterId)) {
            throw new Error(
                `方程 ${equation.equationId} 读取了未声明的参数依赖：${parameterId}`,
            );
        }
    }

    /**
     * 校验一次求解冻结的特征长度。
     *
     * @param characteristicLength 草图特征长度。
     * @throws 当尺度不是有限正数时抛出错误。
     */
    private assertCharacteristicLength(characteristicLength: number): void {
        if (!Number.isFinite(characteristicLength) || characteristicLength <= 0) {
            throw new Error(`草图残差归一化尺度必须是有限正数：${String(characteristicLength)}`);
        }
    }
}
